export type Coordinate = [number, number];
export interface Pipe { id: string; kind: 'manual' | 'simulated'; coordinates: Coordinate[]; plantId?: string }
export interface Plant { id: string; coordinates: Coordinate }
export interface Network { plants: Plant[]; pipes: Pipe[]; calculatedSignature?: string; stale?: boolean;
  /** Horodatage ISO du dernier tracé optimal généré, et son auteur (mock). */
  calculatedAt?: string; calculatedBy?: string }
export type NetworkTool = 'plant' | 'move-plant' | 'remove-plant' | 'draw-pipe' | 'erase-pipe-lasso';

const R = 6371000;
export function distance(a: Coordinate, b: Coordinate): number {
  const rad = Math.PI / 180;
  const h = Math.sin((b[1] - a[1]) * rad / 2) ** 2
    + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin((b[0] - a[0]) * rad / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(Math.min(1, h)));
}
export function networkLength(pipes: Pipe[]): number {
  return Math.round(pipes.reduce((sum, p) => sum + p.coordinates.slice(1)
    .reduce((length, c, i) => length + distance(p.coordinates[i], c), 0), 0));
}
export function initialNetwork(id: string): Network {
  const start: Coordinate = id === 'demo-2' ? [2.20205, 48.8169] : [2.20765, 48.8169];
  const metres = id === 'demo-2' ? 81 : 120;
  return { plants: [], pipes: [{ id: `${id}-fixture`, kind: 'manual',
    coordinates: [start, [start[0], start[1] + metres / R * 180 / Math.PI]] }] };
}
const key = (p: Coordinate) => p.map((v) => v.toFixed(7)).join(',');
export const targetSignature = (buildings: Coordinate[]) => [...new Set(buildings.map(key))].sort().join(';');
export function nearestPlant(plants: Plant[], point: Coordinate): Plant {
  if (!plants.length) throw new Error('Au moins une centrale est nécessaire.');
  return [...plants].sort((a, b) => distance(a.coordinates, point) - distance(b.coordinates, point)
    || a.id.localeCompare(b.id))[0];
}

/**
 * Local illustration only: each building connects to its geographically nearest plant.
 * No roads, obstacles, hydraulics or business costs; NOT a routing optimizer.
 * Existing manual edges are unioned first and never rewritten.
 */
export function simulateConnections(network: Network, buildings: Coordinate[]): Network {
  if (!network.plants.length || !buildings.length) throw new Error('Une centrale et au moins un bâtiment sont nécessaires.');
  const manual = network.pipes.filter((p) => p.kind === 'manual');
  const nodes = [...new Set([...network.plants.map((p) => p.coordinates), ...buildings,
    ...manual.flatMap((p) => p.coordinates)].map(key))].sort();
  const parent = new Map(nodes.map((n) => [n, n]));
  const root = (n: string): string => {
    let current = n;
    while (parent.get(current) !== current) current = parent.get(current)!;
    return current;
  };
  const join = (a: string, b: string) => parent.set(root(a), root(b));
  for (const pipe of manual) pipe.coordinates.slice(1).forEach((p, i) => join(key(pipe.coordinates[i]), key(p)));
  const pipes = [...manual];
  const connect = (point: Coordinate, plant: Plant) => {
    const a = key(point), b = key(plant.coordinates);
    if (root(a) === root(b)) return;
    join(a, b);
    pipes.push({ id: `sim-${a}-${plant.id}`, kind: 'simulated', plantId: plant.id,
      coordinates: [point, plant.coordinates] });
  };
  for (const point of [...buildings].sort((a, b) => key(a).localeCompare(key(b)))) {
    connect(point, nearestPlant(network.plants, point));
  }
  // Any isolated manual component is retained and joined through its closest vertex.
  const vertices = manual.flatMap((p) => p.coordinates).map((point) => ({
    point, plant: nearestPlant(network.plants, point),
  })).sort((a, b) => distance(a.point, a.plant.coordinates) - distance(b.point, b.plant.coordinates)
    || key(a.point).localeCompare(key(b.point)));
  for (const { point, plant } of vertices) {
    if (network.plants.some((p) => root(key(point)) === root(key(p.coordinates)))) continue;
    connect(point, plant);
  }
  return { plants: network.plants, pipes, calculatedSignature: targetSignature(buildings), stale: false };
}
